'use client';

import { Link } from 'react-router-dom';
import { useState } from 'react';
import { User, Trash2 } from 'lucide-react';
import DMCAModal from '@/components/ui/DMCAModal';
import { useAuth } from '@/context/AuthContext';
import ChatwootWidget from '@/components/layout/ChatwootWidget';

const DISCORD_LINK = 'https://discord.gg/mediocretoons';

export default function Footer() {
    const currentYear = new Date().getFullYear();
    const [showDMCAModal, setShowDMCAModal] = useState(false);
    const [showChatwoot, setShowChatwoot] = useState(false);
    const { isAuthenticated, clearSessionAndCache } = useAuth();

    return (
        <footer className="bg-neutral-950 border-t border-neutral-800/50 pb-20 md:pb-0">
            <div className="max-w-7xl mx-auto px-6 py-10">
                {/* Conteúdo Principal */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                    {/* Brand */}
                    <div className="space-y-4">
                        <Link to="/" className="flex items-center gap-3 group w-fit">
                            <div className="relative w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-neutral-900/50 border border-neutral-800 group-hover:border-orange-500/30 transition-colors">
                                <img
                                    src="/logo.png"
                                    alt="MediocreToons Logo"
                                    className="object-contain w-10 h-10"
                                />
                            </div>
                            <span className="text-lg font-bold text-orange-500 group-hover:text-orange-400 transition-colors">
                                MediocreToons
                            </span>
                        </Link>
                        <p className="text-sm text-neutral-400 leading-relaxed max-w-xs">
                            Organize sua coleção e acompanhe o que você curte — tudo num só lugar.
                        </p>
                    </div>

                    {/* Links Rápidos */}
                    <div className="space-y-3">
                        <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Navegação</h3>
                        <nav className="flex flex-col gap-2.5">
                            <Link to="/" className="text-neutral-400 hover:text-orange-400 transition-colors text-sm w-fit">
                                Início
                            </Link>
                            <Link to="/pesquisar" className="text-neutral-400 hover:text-orange-400 transition-colors text-sm w-fit">
                                Pesquisar
                            </Link>
                            <Link to="/colecao" className="text-neutral-400 hover:text-orange-400 transition-colors text-sm w-fit">
                                Minha Coleção
                            </Link>
                            <Link to="/comunidade" className="text-neutral-400 hover:text-orange-400 transition-colors text-sm w-fit">
                                Comunidade
                            </Link>
                        </nav>
                    </div>

                    {/* Redes Sociais e Suporte */}
                    <div className="space-y-3">
                        <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Redes Sociais</h3>
                        <div className="grid grid-cols-2 gap-3 w-full max-w-md md:max-w-none">
                            <a
                                href={DISCORD_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex min-h-[3rem] items-center justify-center gap-2 rounded-xl bg-[#5865F2] px-3 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#4752C4] active:bg-[#3C45A5]"
                                aria-label="Abrir servidor no Discord"
                            >
                                <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C2.451 6.018 1.99 7.765 1.99 9.54c0 5.089 3.158 9.417 7.18 11.174a.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028c4.022-1.757 7.18-6.085 7.18-11.174 0-1.775-.46-3.522-1.648-5.142a.066.066 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                </svg>
                                <span>Discord</span>
                            </a>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowChatwoot(true);
                                    if (window.$chatwoot) {
                                        window.$chatwoot.toggle('open');
                                    }
                                }}
                                className="flex min-h-[3rem] items-center justify-center gap-2 rounded-xl bg-emerald-600 px-3 py-3 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-500 active:bg-emerald-700"
                                aria-label="Abrir suporte via chat"
                            >
                                <User className="h-5 w-5 shrink-0" strokeWidth={2} aria-hidden />
                                <span>Suporte</span>
                            </button>
                        </div>
                        {isAuthenticated && (
                            <div className="pt-4 mt-4 border-t border-white/10">
                                <h3 className="text-white/60 font-semibold text-xs uppercase tracking-wider mb-3">Conta</h3>
                                <button
                                    type="button"
                                    onClick={() => clearSessionAndCache()}
                                    className="flex items-center gap-2 text-neutral-400 hover:text-red-400 transition-colors text-sm w-fit group"
                                    title="Limpar sessão e todo o cache do site"
                                >
                                    <Trash2 className="w-4 h-4 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    <span>Limpar sessão e cache</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Barra Inferior */}
                <div className="pt-6 border-t border-neutral-800/50 relative">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <p className="text-xs text-neutral-500 font-bold">
                            © {currentYear} MediocreToons. Todos os direitos reservados.
                        </p>
                        <div className="flex items-center gap-5 text-xs text-neutral-500">
                            <Link to="/termos" className="hover:text-orange-400 transition-colors font-bold">
                                Termos de uso
                            </Link>
                            <button
                                onClick={() => setShowDMCAModal(true)}
                                className="hover:text-orange-400 transition-colors font-bold"
                            >
                                DMCA
                            </button>
                        </div>
                    </div>
                    {/* Logo com opacidade baixa - canto direito */}
                    <div className="absolute bottom-0 right-0 opacity-10 pointer-events-none">
                        <img
                            src="/favicon.png"
                            alt="MediocreToons Logo"
                            className="object-contain w-[200px] h-[200px]"
                        />
                    </div>
                </div>
            </div>
            <DMCAModal isOpen={showDMCAModal} onClose={() => setShowDMCAModal(false)} />
            <ChatwootWidget visible={showChatwoot} autoOpen />
        </footer>
    );
}

